/*
 * Copyright (c) 2024 Helvia Technologies PC <contact@helvia.io>
 */

import '../../types';

import { AuthorizationRequest, AuthorizationServiceConfiguration, DefaultCrypto, RedirectRequestHandler } from '@openid/appauth';
import { CustomRedirectRequestHandler } from './CustomRedirectRequestHandler';
import { INTEGRATION_GRANT_TYPES } from '../../constants/main';
import { NoHashQueryStringUtils } from './NoHashQueryStringUtils';

export const performOidcAuthorizationRequest = async ({ integration, organizationId, variables, options } = {}) => {
  const { oidcConfiguration, oidcRequest } = await getOidcInfoForStartingAuthorization({ integration, organizationId, variables, options });

  const requestHandler = new RedirectRequestHandler();
  requestHandler.performAuthorizationRequest(oidcConfiguration, oidcRequest);
};

/**
 * Get the information to start an OIDC authorization request.
 *
 * @param {Object} props
 * @param {OpenIdIntegration} props.integration - The integration that includes the OIDC configuration.
 * @param {string} props.organizationId - The organization ID to use in the OIDC request.
 * @param {Object} props.variables - Generic variables that came in from the URL and that you want to pass to the OIDC request and maybe to the post-authentication request.
 * @param {OidcInfoForStartingAuthorizationOptions} props.options - Additional options to pass to the OIDC request.
 */
export const getOidcInfoForStartingAuthorization = async ({ integration, organizationId, variables, options } = {}) => {
  if (!integration) {
    return {
      oidcCodeVerifier: null,
      oidcConfiguration: null,
      oidcRequest: null,
      oidcAuthorizationUrl: null
    };
  }
  const redirectUri = options?.redirectUri
    ? options.redirectUri
    : `${window.location.origin}/login${organizationId ? '?orgId=' + organizationId : ''}`;

  const oidcConfiguration = new AuthorizationServiceConfiguration({
    authorization_endpoint: integration.authorizationEndpoint,
    token_endpoint: integration.tokenEndpoint
  });

  const additionalScopes = integration.additionalScopes || '';
  const usePkce = integration.grantType === INTEGRATION_GRANT_TYPES.AUTHORIZATION_CODE_PKCE;

  const oidcRequest = new AuthorizationRequest(
    {
      client_id: integration.clientId,
      redirect_uri: redirectUri,
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      scope: ['openid', additionalScopes].join(' ').trim(),
      state: getOidcRequestState(integration.id, organizationId, variables),
      extras: {
        response_mode: 'query',
        ...(options?.extras || {})
      }
    },
    new DefaultCrypto(),
    usePkce
  );
  const requestJson = await oidcRequest.toJson();

  const requestHandler = new RedirectRequestHandler();
  const url = requestHandler.buildRequestUrl(oidcConfiguration, oidcRequest);

  return {
    oidcCodeVerifier: requestJson?.internal?.code_verifier,
    oidcConfiguration,
    oidcRequest,
    oidcAuthorizationUrl: url,
    oidcRedirectUrl: redirectUri
  };
};

/**
 * Get the state to use in an OIDC request.
 */
export const getOidcRequestState = (integrationId, organizationId, variables) => {
  const params = {
    integrationId,
    orgId: organizationId,
    ...(variables || {})
  };
  const searchParams = new URLSearchParams(params);
  return searchParams.toString();
};

/**
 * Parse the state returned from an OIDC request.
 * @param {*} state - The state returned from an OIDC request.
 * @returns {Object} - An object with the integrationId and organizationId.
 */
export const parseOidcState = (state) => {
  if (!state) {
    return {};
  }
  const urlSearchParams = new URLSearchParams(state);
  const { integrationId, orgId, ...variables } = Object.fromEntries(urlSearchParams.entries());
  return {
    integrationId,
    organizationId: orgId,
    variables
  };
};

/**
 * If you have started an OIDC authorization request using redirects (not a popup), you can use this function to
 * get the information that will make you able to complete it.
 *
 * @param {IntegrationCallback} listener - A function to be called when the authorization request is complete.
 */
export const completeOidcAuthorizationRequestIfPossible = (listener) => {
  const authorizationHandler = new CustomRedirectRequestHandler(undefined, new NoHashQueryStringUtils());
  authorizationHandler.completeAuthorizationRequestIfPossible().then((result) => {
    if (listener) {
      const { request, response } = result || {};
      if (response) {
        const redirectUri = request.redirectUri;
        const codeVerifier = request?.internal?.code_verifier;
        const code = response.code;
        const { integrationId, organizationId, variables } = parseOidcState(response.state);

        if (code && redirectUri && integrationId && organizationId) {
          const payload = {
            code,
            redirectUri,
            codeVerifier,
            integrationId,
            organizationId
          };
          if (!variables || (variables && Object.keys(variables).length > 0)) {
            payload.variables = variables;
          }
          listener(payload);
        }
      } else {
        listener(null);
      }
    }
  });
};
